import type { DictFragment } from '../translations';

/**
 * Shared, generic UI strings reused across the whole app.
 * ONLY generic vocabulary belongs here — anything page-specific goes in that
 * area's own fragment. Never redefine a `common.*` key elsewhere.
 */
export const common: DictFragment = {
  fr: {
    // actions
    'common.save': 'Enregistrer',
    'common.saving': 'Enregistrement…',
    'common.saved': 'Enregistré',
    'common.cancel': 'Annuler',
    'common.add': 'Ajouter',
    'common.create': 'Créer',
    'common.edit': 'Modifier',
    'common.delete': 'Supprimer',
    'common.remove': 'Retirer',
    'common.close': 'Fermer',
    'common.back': 'Retour',
    'common.confirm': 'Confirmer',
    'common.search': 'Rechercher',
    'common.filter': 'Filtrer',
    'common.clear': 'Effacer',
    'common.apply': 'Appliquer',
    'common.reset': 'Réinitialiser',
    'common.refresh': 'Actualiser',
    'common.export': 'Exporter',
    'common.import': 'Importer',
    'common.open': 'Ouvrir',
    'common.view': 'Voir',
    'common.select': 'Sélectionner',
    'common.selectEllipsis': 'Sélectionner…',
    'common.all': 'Tous',
    'common.none': 'Aucun',
    'common.yes': 'Oui',
    'common.no': 'Non',
    'common.ok': 'OK',
    'common.done': 'Terminé',
    'common.next': 'Suivant',
    'common.previous': 'Précédent',
    'common.more': 'Plus',
    'common.less': 'Moins',
    'common.copy': 'Copier',
    'common.copied': 'Copié',
    'common.download': 'Télécharger',
    'common.upload': 'Téléverser',
    'common.retry': 'Réessayer',
    'common.send': 'Envoyer',
    'common.submit': 'Soumettre',
    'common.rename': 'Renommer',
    'common.duplicate': 'Dupliquer',
    'common.actions': 'Actions',
    // states
    'common.loading': 'Chargement…',
    'common.saveFailed': 'Échec de l’enregistrement',
    'common.loadFailed': 'Échec du chargement',
    'common.error': 'Erreur',
    'common.errorGeneric': 'Une erreur est survenue',
    'common.empty': 'Aucun élément',
    'common.noResults': 'Aucun résultat',
    'common.required': 'Requis',
    'common.optional': 'Facultatif',
    'common.unsaved': 'Modifications non enregistrées',
    // fields / labels
    'common.name': 'Nom',
    'common.title': 'Titre',
    'common.description': 'Description',
    'common.status': 'Statut',
    'common.date': 'Date',
    'common.dateFormatPlaceholder': 'jj.mm.aa',
    'common.type': 'Type',
    'common.notes': 'Notes',
    'common.details': 'Détails',
    'common.email': 'E-mail',
    'common.comment': 'Commentaire',
    'common.comments': 'Commentaires',
    // shell / topbar
    'common.logout': 'Déconnexion',
    'common.language': 'Langue',
    'common.theme.toLight': 'Passer en mode clair',
    'common.theme.toDark': 'Passer en mode sombre',
    // confirm defaults
    'common.confirmDelete': 'Confirmer la suppression',
    'common.confirmDeleteMessage': 'Cette action est irréversible. Voulez-vous continuer ?',
    // project card
    'common.pmoAccompanistAbbrev': 'Acc.',
  },
  en: {
    'common.save': 'Save',
    'common.saving': 'Saving…',
    'common.saved': 'Saved',
    'common.cancel': 'Cancel',
    'common.add': 'Add',
    'common.create': 'Create',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.remove': 'Remove',
    'common.close': 'Close',
    'common.back': 'Back',
    'common.confirm': 'Confirm',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.clear': 'Clear',
    'common.apply': 'Apply',
    'common.reset': 'Reset',
    'common.refresh': 'Refresh',
    'common.export': 'Export',
    'common.import': 'Import',
    'common.open': 'Open',
    'common.view': 'View',
    'common.select': 'Select',
    'common.selectEllipsis': 'Select…',
    'common.all': 'All',
    'common.none': 'None',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.ok': 'OK',
    'common.done': 'Done',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.more': 'More',
    'common.less': 'Less',
    'common.copy': 'Copy',
    'common.copied': 'Copied',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.retry': 'Retry',
    'common.send': 'Send',
    'common.submit': 'Submit',
    'common.rename': 'Rename',
    'common.duplicate': 'Duplicate',
    'common.actions': 'Actions',
    'common.loading': 'Loading…',
    'common.saveFailed': 'Save failed',
    'common.loadFailed': 'Failed to load',
    'common.error': 'Error',
    'common.errorGeneric': 'Something went wrong',
    'common.empty': 'No items',
    'common.noResults': 'No results',
    'common.required': 'Required',
    'common.optional': 'Optional',
    'common.unsaved': 'Unsaved changes',
    'common.name': 'Name',
    'common.title': 'Title',
    'common.description': 'Description',
    'common.status': 'Status',
    'common.date': 'Date',
    'common.dateFormatPlaceholder': 'dd.mm.yy',
    'common.type': 'Type',
    'common.notes': 'Notes',
    'common.details': 'Details',
    'common.email': 'Email',
    'common.comment': 'Comment',
    'common.comments': 'Comments',
    'common.logout': 'Logout',
    'common.language': 'Language',
    'common.theme.toLight': 'Switch to light mode',
    'common.theme.toDark': 'Switch to dark mode',
    'common.confirmDelete': 'Confirm deletion',
    'common.confirmDeleteMessage': 'This action cannot be undone. Do you want to continue?',
    'common.pmoAccompanistAbbrev': 'Acc.',
  },
};
